<script>
import userApi from '@/api/user';
import utils from '@/utils/common';
import DefaultLayout from '@/layouts/Default.vue';
import { getUserStatus, userStatuses } from '../utils/userStatus';

import CommonSection from '../components/CommonSection.vue';
import AdminUserListStatusCell from '../components/admin/list/AdminUserListStatusCell.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseTable from '../components/base/BaseTable.vue';
import FormBlock from '../components/FormBlock.vue';
import BaseTableCell from '../components/base/BaseTableCell.vue';

const priceFormatter = new Intl.NumberFormat('ru', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0 });

const formatDate = (date) => (date ? utils.convertDateToString(date) : '\u2013');

const sumConfirmedPayments = (user) => user.payments
  .filter((payment) => payment.status === 'confirmed')
  .map((confirmed) => confirmed.amount)
  .reduce((acc, value) => acc + value, 0);

const promoCodesFormatter = (user) => {
  const promoCodes = user.customInfo.usedPromoCodes ?? [];
  return promoCodes.length ? promoCodes.join(', ') : '\u2013';
};

const setUserName = (user) => (user.name && user.surname) ? user.name.replace(/ /g, '') + ' ' + user.surname.replace(/ /g, '') : user.email; // eslint-disable-line

const headers = [
  {                                             component: { is: BaseTableCell,           props: (item) => ({ value: item.userNameFormatted, isBold: true, isLarge: true }) }                                     }, // eslint-disable-line
  { title: 'Статус',                            component: { is: AdminUserListStatusCell, props: (item) => ({ row: item }) }                                                                                      }, // eslint-disable-line
  { title: 'Тариф',                             component: { is: BaseTableCell,           props: (item) => ({ value: item.tariff.name }) }                                                                        }, // eslint-disable-line
  { title: 'Регистрация',                       component: { is: BaseTableCell,           props: (item) => ({ value: item.createdAtFormatted }) }                                                                 }, // eslint-disable-line
  { title: 'Баланс',                            component: { is: BaseTableCell,           props: (item) => ({ value: item.accountBalanceFormatted, isBold: true }) }                                              }, // eslint-disable-line
  { title: 'Последний платёж',                  component: { is: BaseTableCell,           props: (item) => ({ value: item.lastPaymentFormatted, isBold: true }) }                                                 }, // eslint-disable-line
  { title: 'Дата последнего платежа',           component: { is: BaseTableCell,           props: (item) => ({ value: item.lastPaymentDate }) }                                                                    }, // eslint-disable-line
  { title: 'Всего оплачено',                    component: { is: BaseTableCell,           props: (item) => ({ value: item.paymentsSumFormatted, isBold: true }) }                                                 }, // eslint-disable-line
  { title: 'Всего платежей',                    component: { is: BaseTableCell,           props: (item) => ({ value: item.totalPayments }) }                                                                      }, // eslint-disable-line
  { title: 'Использованные промокоды',          component: { is: BaseTableCell,           props: (item) => ({ value: item.promoCodesFormatted }) }                                                                }, // eslint-disable-line
  { title: 'Проекты',                           component: { is: BaseTableCell,           props: (item) => ({ value: item.projectsFormatted }) }                                                                  }, // eslint-disable-line
  { title: 'Дата блокировки',                   component: { is: BaseTableCell,           props: (item) => ({ value: item.blockedAtFormatted }) }                                                                 }, // eslint-disable-line
];

const paymentHeaders = [
  { title: 'ID',                        component: { is: BaseTableCell,           props: (item) => ({ value: item.id }) },                              sortFn: (a, b) => b.id - a.id, defaultSort: true, required: true, width: 4  }, // eslint-disable-line
  { title: 'Сумма',                     component: { is: BaseTableCell,           props: (item) => ({ value: item.sumFormatted, isBold: true }) },      sortFn: (a, b) => a.amount - b.amount, width: 48                            }, // eslint-disable-line
  { title: 'Дата',                      component: { is: BaseTableCell,           props: (item) => ({ value: item.createdAtFormatted }) },              sortFn: (a, b) => a.timeToSort - b.timeToSort, width: 48                    }, // eslint-disable-line
];

export default {
  name: 'AdminDetailPage',
  components: {
    FormBlock,
    BaseTable,
    BaseInput,
    BaseButton,
    DefaultLayout,
    CommonSection,
    BaseSelect,
  },
  data() {
    return {
      user: null,
      balanceIncrease: false,
      newBalance: 0,
      payments: [],
    };
  },
  created() {
    this.$store.dispatch('tariff/fetchItems');
    this.fetchUser();
  },
  computed: {
    tariffOptions() { return this.$store.state.tariff.items.map((t) => ({ label: t.name, value: t['@id'] })); },
    statusOptions() { return userStatuses; },
    headers() { return headers; },
    tariffs() { return this.$store.state.tariff.items; },
    paymentHeaders() { return paymentHeaders; },
    confirmedPayments() {
      return (this.user.payments ?? [])
        .filter((payment) => payment.status === 'confirmed' && payment.createdAt)
        .map((p) => ({
          ...p,
          sumFormatted: priceFormatter.format(Math.round(p.amount / 100)),
          createdAtFormatted: utils.convertDateToString(new Date(p.createdAt), 'short'),
          timeToSort: new Date(p.createdAt).getTime(),
        }));
    },
    modifiedUser() {
      const payments = (this.user.payments ?? [])
        .filter(p => p.status === 'confirmed' && p.createdAt);

      const lastPayment = [...payments].pop();
      const userName = setUserName(this.user);

      return {
        ...this.user,
        createdAt: utils.convertDateToString(new Date(this.user.createdAt), 'short'),
        selectedTariff: this.user.tariff['@id'],
        lastPayment: this.findLastPayment(this.user.payments),
        lastPaymentDate: lastPayment?.createdAt ? utils.convertDateToString(new Date(lastPayment?.createdAt), 'short') : '—',
        totalPayments: this.findTotalPaymentsCount(this.user.payments),
        status: getUserStatus(this.user),
        lastPaymentFormatted: lastPayment ? priceFormatter.format(Math.round(lastPayment.amount / 100)) : '\u2013',
        userNameFormatted: userName,
        createdAtFormatted: this.user.createdAt ? formatDate(new Date(this.user.createdAt)) : '\u2013',
        accountBalanceFormatted: priceFormatter.format(Math.round(this.user.accountBalance / 100)),
        paymentsSumFormatted: priceFormatter.format(Math.round(sumConfirmedPayments(this.user) / 100)),
        promoCodesFormatted: promoCodesFormatter(this.user),
        projectsFormatted: `${this.user.projectCount} / ${this.user.tariff.maxProjects}`,
        blockedAtFormatted: this.user.accountBlockedAt ? formatDate(new Date(this.user.accountBlockedAt)) : '\u2013',
      };
    },
    userName() {
       return (this.user.name && this.user.surname) ? this.user.name.replace(/ /g, '') + ' ' + this.user.surname.replace(/ /g, '') : this.user.email; // eslint-disable-line
    },
  },
  methods: {
    fetchUser() {
      userApi.fetchItem(this.$route.params.userId, {}, true)
        .then((r) => {
          this.user = r;
        });
    },
    findLastPayment(payments) {
      const confirmedPayments = (payments ?? [])
        .filter(p => p.status === 'confirmed' && p.createdAt)
        .map(p => ({ amount: p.amount, date: formatDate(new Date(p.createdAt)) }));

      return confirmedPayments.pop();
    },
    findTotalPaymentsCount(payments) {
      const confirmedPayments = (payments ?? []).filter((payment) => payment.status === 'confirmed');
      return confirmedPayments.length;
    },
    async changeTariff(e) {
      const newTariff = this.tariffs.find((tariff) => tariff['@id'] === e);

      if (newTariff && await this.$confirm(`Вы действительно хотите сменить пользователю [${this.userName}] тариф на [${newTariff.name}]?`)) {
        userApi.update(+this.$route.params.userId, { tariff: newTariff['@id'] }).then(() => { this.fetchUser(); });
      }
    },
    async increaseBalance() {
      const newBalance = +this.newBalance;

      if (await this.$confirm(`Вы действительно хотите начислить пользователю [${this.userName}] средства в размере [${new Intl.NumberFormat('ru', { style: 'currency', currency: 'RUB' }).format(newBalance)}]?`)) {
        userApi.update(+this.$route.params.userId, { accountBalance: newBalance * 100 + this.user.accountBalance }).then(() => {
          this.fetchUser();
          this.newBalance = 0;
          this.balanceIncrease = false;
        });
      }
    },
    async toggleBlocked() {
      if (this.user.accountBlocked && await this.$confirm(`Вы действительно хотите разблокировать пользователя [${this.userName}]?`)) {
        userApi.update(+this.$route.params.userId, { accountBlocked: false }).then(() => { this.fetchUser(); });
      }

      if (!this.user.accountBlocked && await this.$confirm(`Вы действительно хотите заблокировать пользователя [${this.userName}]?`)) {
        userApi.update(+this.$route.params.userId, { accountBlocked: true, accountBlockedAt: new Date() }).then(() => { this.fetchUser(); });
      }
    },
    async confirmAccount() {
      if (!this.user.accountConfirmed && await this.$confirm(`Вы действительно хотите активировать пользователя [${this.userName}]?`)) {
        userApi.update(+this.$route.params.userId, { accountConfirmed: true }).then(() => { this.fetchUser(); });
      }
    },
  },
};
</script>

<template lang="pug">
DefaultLayout
  .b-user-page(v-if="user")
    .user
      CommonSection(title="Информация")
      .info
        .row(v-for="header in headers" :key="header.title")
          span.sub-headliner(v-if="header.title" ) {{ header.title }}:
          .content
            component(:is="header.component.is" v-bind="header.component.props(modifiedUser)")
      .management
        CommonSection(title="Управление аккаунтом")
          .option
            span.sub-headliner Сменить тариф:
            BaseSelect(:value="user.tariff.name" :label="user.tariff.name" :options="tariffOptions" @change="changeTariff" class="selector")
          .option
            BaseButton(v-if="!balanceIncrease" @click="balanceIncrease = true") Начислить средства
            .input(v-if="balanceIncrease" )
              BaseInput(v-model="newBalance" )
              BaseButton(@click="increaseBalance") Начислить
          .option
            BaseButton(v-if="user.accountBlocked" :green="true" @click="toggleBlocked") Разблокировать
            BaseButton(v-if="!user.accountBlocked" :grey="true" @click="toggleBlocked") Заблокировать
          .option
            BaseButton(v-if="!user.accountConfirmed" :green="true" @click="confirmAccount") Акитвировать
      .tables
        CommonSection(title="Платежи" )
          FormBlock
            BaseTable(:headers="paymentHeaders" :items="confirmedPayments")

</template>

<style scoped lang="scss">
.user-page {
  display: flex;
  flex-direction: column;
  gap: 10px;

  .user {
    display: flex;
    flex-direction: column;
    gap: 50px;
  }
  .info {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .management {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .option {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .selector {
    width: 200px;
  }
  .row {
    display: flex;
    gap: 10px;
  }
  .headliner {
    font-weight: 700;
    font-size: 24px;
  }
  .sub-headliner {
    font-weight: 700;
  }
}
</style>
